(() => {
  const FORCE_FLAG = 'pitb-notify-now';
  const SUBMITTED_KEY = 'pitb-forms-submitted';
  const TOAST_ID = 'pitb-notify-toast';

  // Session dismiss/minimize + Forms helpers — never wipe post-submit markers.
  const FORMS_CLEAR_RE =
    /^(forms_|shopify.?forms|ShopifyForms)|dismissed-in-session$|minimized-in-session$|form-embed|forms-root|forms_visit|forms_fallback|forms_previous|forms_session/i;

  const isClearableFormsKey = (key) => {
    if (!key) return false;
    if (key === FORCE_FLAG || key === SUBMITTED_KEY) return false;
    if (key.endsWith('-dismissed-after-submit')) return false;
    if (FORMS_CLEAR_RE.test(key)) return true;
    const lower = key.toLowerCase();
    return (
      lower.includes('shopifyforms') ||
      lower.includes('shopify-forms') ||
      lower.includes('shopify_forms') ||
      lower.startsWith('forms_') ||
      lower.endsWith('-dismissed-in-session') ||
      lower.endsWith('-minimized-in-session')
    );
  };

  const markSubmitted = () => {
    try {
      window.localStorage.setItem(SUBMITTED_KEY, '1');
    } catch (e) {
      /* ignore */
    }
  };

  const hasAlreadySubmitted = () => {
    try {
      if (window.localStorage.getItem(SUBMITTED_KEY) === '1') return true;
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i);
        if (key && key.endsWith('-dismissed-after-submit')) {
          markSubmitted();
          return true;
        }
      }
    } catch (e) {
      /* ignore */
    }
    return false;
  };

  // Keep our flag in sync if Forms writes its post-submit key.
  const watchSubmittedFlag = () => {
    if (hasAlreadySubmitted()) return;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (hasAlreadySubmitted() || tries >= 120) window.clearInterval(timer);
    }, 1000);

    document.addEventListener(
      'click',
      () => {
        window.setTimeout(() => {
          if (hasAlreadySubmitted()) return;
          const embeds = document.querySelectorAll('shopify-forms-embed');
          embeds.forEach((embed) => {
            try {
              const text = (embed.shadowRoot && embed.shadowRoot.textContent) || '';
              if (/welcome to the fandom|successfully subscribed|thanks for subscribing|you.?re subscribed/i.test(text)) {
                markSubmitted();
              }
            } catch (e) {
              /* ignore */
            }
          });
        }, 800);
      },
      true
    );
  };

  const showAlreadySubmittedMessage = (anchor) => {
    let msg = document.getElementById(TOAST_ID);
    if (!msg) {
      msg = document.createElement('div');
      msg.id = TOAST_ID;
      msg.className = 'pitb-notify-msg';
      msg.setAttribute('role', 'status');
      msg.setAttribute('aria-live', 'polite');
      document.body.appendChild(msg);
    }

    msg.innerHTML =
      '<span class="pitb-notify-msg__title">You\'re already on the list</span>' +
      '<span class="pitb-notify-msg__text">We\'ll email you about upcoming drops and events.</span>';

    const placeNear = (el) => {
      const target = el || document.querySelector('[data-pitb-notify]');
      if (!target) {
        msg.classList.add('pitb-notify-msg--fallback');
        msg.style.left = '';
        msg.style.top = '';
        msg.style.right = '';
        msg.style.bottom = '';
        return;
      }

      msg.classList.remove('pitb-notify-msg--fallback');
      const rect = target.getBoundingClientRect();
      const gap = 10;
      const toastWidth = Math.min(320, window.innerWidth - 24);

      msg.style.width = `${toastWidth}px`;
      msg.style.maxWidth = `${toastWidth}px`;
      msg.classList.add('is-measuring');
      msg.classList.add('is-visible');
      const toastHeight = msg.offsetHeight || 64;
      msg.classList.remove('is-measuring');

      let top;
      if (rect.top - gap - toastHeight < 12) {
        top = rect.bottom + gap;
        msg.classList.add('pitb-notify-msg--below');
      } else {
        top = rect.top - gap - toastHeight;
        msg.classList.remove('pitb-notify-msg--below');
      }

      let left = rect.right - toastWidth;
      left = Math.max(12, Math.min(left, window.innerWidth - toastWidth - 12));

      msg.style.left = `${Math.round(left)}px`;
      msg.style.top = `${Math.round(top)}px`;
      msg.style.right = 'auto';
      msg.style.bottom = 'auto';
      msg.style.transform = 'none';
    };

    placeNear(anchor);
    msg.classList.add('is-visible');

    window.clearTimeout(showAlreadySubmittedMessage._timer);
    showAlreadySubmittedMessage._timer = window.setTimeout(() => {
      msg.classList.remove('is-visible');
    }, 4800);

    window.clearTimeout(showAlreadySubmittedMessage._reposition);
    const onScrollOrResize = () => placeNear(anchor || document.querySelector('[data-pitb-notify]'));
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    showAlreadySubmittedMessage._reposition = window.setTimeout(() => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    }, 4800);
  };

  const clearAllFormsLocalFlags = () => {
    const scrub = (storage) => {
      try {
        const keys = [];
        for (let i = 0; i < storage.length; i += 1) {
          const key = storage.key(i);
          if (isClearableFormsKey(key)) keys.push(key);
        }
        keys.forEach((key) => storage.removeItem(key));
      } catch (e) {
        /* ignore */
      }
    };

    scrub(window.localStorage);
    scrub(window.sessionStorage);

    [
      'forms_visit_count',
      'forms_previous_session_id',
      'forms_fallback_session_id',
      'forms_session_id',
    ].forEach((key) => {
      try {
        window.localStorage.removeItem(key);
        window.sessionStorage.removeItem(key);
      } catch (e) {
        /* ignore */
      }
    });
  };

  const clearNotifyHash = () => {
    if (window.location.hash !== '#pitb-notify') return;
    try {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch (e) {
      /* ignore */
    }
  };

  const finishForceOpen = () => {
    window.__PITB_NOTIFY_FORCE = false;
    try {
      window.sessionStorage.removeItem(FORCE_FLAG);
    } catch (e) {
      /* ignore */
    }
    clearNotifyHash();
  };

  const clickEl = (el) => {
    if (!el) return false;
    el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    return true;
  };

  const openShopifyForms = () => {
    const embeds = document.querySelectorAll('shopify-forms-embed');
    const containers = document.querySelectorAll('[id^="app-embed-container-"]');
    if (!embeds.length && !containers.length) return false;

    let opened = false;

    containers.forEach((container) => {
      try {
        container.style.display = 'block';
        container.removeAttribute('hidden');
      } catch (e) {
        /* ignore */
      }
    });

    embeds.forEach((embed) => {
      try {
        embed.style.display = 'block';
        embed.setAttribute('reveal', 'true');
        embed.removeAttribute('hidden');

        const root = embed.shadowRoot;
        if (!root) {
          opened = true;
          return;
        }

        const candidates = [
          ...root.querySelectorAll('button[class*="_teaser"]'),
          ...root.querySelectorAll('[class*="teaser"] button'),
          ...root.querySelectorAll('button[class*="teaser"]'),
          ...root.querySelectorAll('button'),
        ];

        for (const btn of candidates) {
          const label = `${btn.textContent || ''} ${btn.getAttribute('aria-label') || ''}`.toLowerCase();
          if (/close|dismiss/.test(label)) continue;
          if (clickEl(btn)) {
            opened = true;
            break;
          }
        }
      } catch (e) {
        /* ignore */
      }
    });

    return opened || embeds.length > 0 || containers.length > 0;
  };

  const reloadForImmediateOpen = () => {
    try {
      window.sessionStorage.setItem(FORCE_FLAG, '1');
    } catch (e) {
      /* ignore */
    }
    window.location.reload();
  };

  const openWithRetry = ({ fromReload, anchor } = {}) => {
    if (hasAlreadySubmitted()) {
      finishForceOpen();
      showAlreadySubmittedMessage(anchor);
      return;
    }

    if (!fromReload) clearAllFormsLocalFlags();

    if (openShopifyForms()) {
      finishForceOpen();
      return;
    }

    if (fromReload || window.__PITB_NOTIFY_FORCE) {
      let tries = 0;
      const timer = window.setInterval(() => {
        tries += 1;
        if (hasAlreadySubmitted()) {
          window.clearInterval(timer);
          finishForceOpen();
          showAlreadySubmittedMessage();
          return;
        }
        if (openShopifyForms() || tries >= 40) {
          window.clearInterval(timer);
          finishForceOpen();
        }
      }, 100);
      return;
    }

    reloadForImmediateOpen();
  };

  window.PITBNotify = {
    open: (anchor) => openWithRetry({ fromReload: false, anchor }),
    clearFormsFlags: clearAllFormsLocalFlags,
    hasSubmitted: hasAlreadySubmitted,
  };

  document.addEventListener('click', (event) => {
    const trigger =
      event.target.closest('[data-pitb-notify]') ||
      event.target.closest('a[href="#pitb-notify"]') ||
      event.target.closest('a[href$="#pitb-notify"]');
    if (!trigger) return;
    event.preventDefault();
    openWithRetry({ fromReload: false, anchor: trigger });
  });

  if (window.__PITB_NOTIFY_FORCE) {
    window.setTimeout(() => openWithRetry({ fromReload: true }), 200);
  } else {
    clearNotifyHash();
  }

  watchSubmittedFlag();
})();
